import { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { closeCreateModal, createSection } from '@/features/sections/sectionsSlice';
import { showToast } from '@/features/ui/uiSlice';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import styles from './CreateSectionModal.module.css';

export default function CreateSectionModal() {
  const dispatch = useAppDispatch();
  const createLoading = useAppSelector((s) => s.sections.createLoading);
  const createError = useAppSelector((s) => s.sections.createError);

  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus name input on open
    setTimeout(() => inputRef.current?.focus(), 60);
  }, []);

  const handleClose = () => dispatch(closeCreateModal());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const result = await dispatch(createSection({ name: trimmed }));
    if (createSection.fulfilled.match(result)) {
      trackEvent(ANALYTICS_EVENTS.SECTION_CREATE, { name: trimmed });
      dispatch(showToast({ title: 'Collection created', message: `"${trimmed}" is ready to use.` }));
    }
  };

  return (
    <div className={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="create-section-title">
        {/* Header */}
        <div className={styles.header}>
          <span id="create-section-title" className={styles.headerTitle}>New Collection</span>
          <button className={styles.closeBtn} onClick={handleClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className={styles.body}>
            {/* Name */}
            <div>
              <p className={styles.fieldLabel}>Name</p>
              <input
                ref={inputRef}
                className={styles.input}
                type="text"
                placeholder="e.g. My Favourites"
                maxLength={255}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {/* Error */}
            {createError && <p className={styles.error}>{createError}</p>}
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <button type="button" className={styles.cancelBtn} onClick={handleClose}>Cancel</button>
            <button type="submit" className={styles.createBtn} disabled={createLoading || !name.trim()}>
              {createLoading ? 'Creating…' : 'Create collection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
